import type { VoiceSessionTurn } from "@magnus/contract/messages";
import {
  createLiveTranscript,
  type LiveFunctionCallLoop,
  type LiveVoice,
} from "@workspace/voice-shared/live";
import type { MagnusChatToolRunner } from "@workspace/voice-shared/magnus-chat-tool";
import type {
  LiveCallDataChannel,
  LiveCallMedia,
  LiveCallPeerConnection,
  LiveCallSender,
  LiveCallStream,
} from "./platform";

type Delegation = NonNullable<VoiceSessionTurn["delegations"]>[number];

/** One call, across every provider leg a voice switch gives it. */
export type Call = {
  sessionId: string;
  startedAt: string;
  sessionEpoch: number;
  callId?: string;
  chatId?: string;
  voice: LiveVoice;
  greeting: string;
  pc: LiveCallPeerConnection | null;
  dc: LiveCallDataChannel | null;
  sender: LiveCallSender | null;
  media: LiveCallMedia | null;
  /** Aborts the in-flight mint on hangup. */
  minting: AbortController;
  ready: boolean;
  swapping: boolean;
  ended: boolean;
  legStartMs: number;
  notes: string[];
  activeQuery: string | null;
  stream: LiveCallStream | null;
  tick: ReturnType<typeof setInterval> | null;
  startTimer: ReturnType<typeof setTimeout> | null;
  disconnectTimer: ReturnType<typeof setTimeout> | null;
  loop: LiveFunctionCallLoop | null;
  runner: MagnusChatToolRunner | null;
  transcript: ReturnType<typeof createLiveTranscript>;
  delegations: Map<string, { atMs: number; marker: Delegation }>;
  cancelled: boolean;
  /** The user's turn as it stood when the running delegation started. */
  saidAtDelegation: string;
  backendBusy: boolean;
  lastUserSpeech: number;
  lastAssistantSpeech: number;
  lastActivity: number;
};

export function newCall(init: {
  sessionId: string;
  chatId?: string;
  voice: LiveVoice;
}): Call {
  const now = Date.now();
  return {
    ...init,
    startedAt: new Date(now).toISOString(),
    sessionEpoch: now,
    greeting: "",
    pc: null,
    dc: null,
    sender: null,
    media: null,
    minting: new AbortController(),
    ready: false,
    swapping: false,
    ended: false,
    legStartMs: 0,
    notes: [],
    activeQuery: null,
    stream: null,
    tick: null,
    startTimer: null,
    disconnectTimer: null,
    loop: null,
    runner: null,
    transcript: createLiveTranscript(),
    delegations: new Map(),
    cancelled: false,
    saidAtDelegation: "",
    backendBusy: false,
    lastUserSpeech: 0,
    lastAssistantSpeech: 0,
    lastActivity: now,
  };
}

/** What the user said after `before`, or all of it once a new turn began. */
export function saidSince(said: string, before: string): string {
  return before && said.startsWith(before) ? said.slice(before.length) : said;
}
